import { useEffect, useRef, useState } from 'react';

import { graphql } from 'react-relay';
import { useRelayEnvironment } from 'react-relay/hooks';
import { fetchQuery, type Subscription } from 'relay-runtime';

import type { useHydratedTeamAllFieldTypesActionQuery } from './__generated__/useHydratedTeamAllFieldTypesActionQuery.graphql';

export type HydratedTeam = { teamId: string; displayName: string };

export type HydratedTeamResult = {
	team: HydratedTeam | null;
	isLoading: boolean;
	error: Error | null;
};

export function useHydratedTeam(
	id: string | null | undefined,
	cloudId: string,
	shouldHydrate: boolean,
	onInvalidAction?: () => void,
): HydratedTeamResult | null {
	const environment = useRelayEnvironment();
	const [team, setTeam] = useState<HydratedTeam | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);
	const inFlightFor = useRef<string | null>(null);
	const activeSubscription = useRef<Subscription | null>(null);
	const disposed = useRef<boolean>(false);

	useEffect(() => {
		disposed.current = false;
		return () => {
			disposed.current = true;
			activeSubscription.current?.unsubscribe();
			activeSubscription.current = null;
			inFlightFor.current = null;
		};
	}, []);

	useEffect(() => {
		if (!id || !cloudId || !shouldHydrate) {
			// reset state for missing id and do not issue a query
			if (inFlightFor.current) {
				activeSubscription.current?.unsubscribe();
				activeSubscription.current = null;
				inFlightFor.current = null;
			}
			setTeam(null);
			setIsLoading(false);
			setError(null);
			return;
		}

		if (team && team.teamId === id && team.displayName) {
			return; // already hydrated fully
		}
		if (inFlightFor.current === id) {
			return; // request already in flight for this id
		}

		// cancel any previous in-flight subscription for a different id
		if (activeSubscription.current) {
			activeSubscription.current.unsubscribe();
			activeSubscription.current = null;
		}

		inFlightFor.current = id;
		setIsLoading(true);
		setError(null);

		const subscription = fetchQuery<useHydratedTeamAllFieldTypesActionQuery>(
			environment,
			graphql`
				query useHydratedTeamAllFieldTypesActionQuery($id: ID!, $siteId: String!) {
					team {
						teamV2(id: $id, siteId: $siteId) {
							id
							displayName
						}
					}
				}
			`,
			{ id, siteId: cloudId },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (response) => {
				if (disposed.current) {
					return;
				}
				if (inFlightFor.current !== id) {
					return;
				} // stale
				if (response?.team?.teamV2) {
					setTeam({
						teamId: response.team.teamV2.id,
						displayName: response.team.teamV2.displayName ?? '',
					});
				} else {
					setTeam(null);
					onInvalidAction?.();
				}
				setIsLoading(false);
			},
			error: (error: Error) => {
				if (disposed.current) {
					return;
				}
				if (inFlightFor.current === id) {
					setError(error);
					setIsLoading(false);
					onInvalidAction?.();
				}
			},
			complete: () => {
				if (!disposed.current && inFlightFor.current === id) {
					setIsLoading(false);
				}
			},
		});

		activeSubscription.current = subscription;

		return () => {
			if (activeSubscription.current === subscription) {
				subscription.unsubscribe();
				activeSubscription.current = null;
				if (inFlightFor.current === id) {
					inFlightFor.current = null;
				}
			}
		};
		// We intentionally exclude user/isLoading from deps to avoid refetch loops
	}, [id, cloudId, environment]); // eslint-disable-line react-hooks/exhaustive-deps

	if (!id) {
		return null; // maintain external contract returning null when no team id supplied
	}

	return { team, isLoading, error };
}
